/**
 * PricingSliderSection — "Sign Your Contract" landing-page pricing tier.
 *
 * Landing-page section variant of the slider experience previewed at
 * /pricing-slider-mockup. Toggled by `content.pricing.mode === 'slider'`
 * in the CMS; default stays on the existing PricingSection cards.
 *
 * The route /pricing-slider-mockup remains as a private sandbox URL so
 * we can keep iterating on slider design without touching the live UX.
 *
 * Props mirror PricingSection.js so this slots in as a drop-in section.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronRight, Check, Ticket, TrendingDown, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FoundersCapIcon } from '../icons/FoundersCapIcon';
import { trackPricingClick } from '../../utils/analytics';
import '../../pages/PricingSliderMockup.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Animate a numeric `target` over `duration` ms using ease-out cubic.
 * Drives the digit-roll on Per Week, Total, Savings, and the strikethrough.
 */
function useTweenedNumber(target, duration = 350) {
  const [value, setValue] = useState(target);
  useEffect(() => {
    let raf;
    const start = value;
    const end = target;
    const t0 = performance.now();
    const tick = (now) => {
      const elapsed = now - t0;
      const tFrac = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - tFrac, 3);
      setValue(start + (end - start) * eased);
      if (tFrac < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return value;
}

export function PricingSliderSection({ content, theme, t, pricingCurrency, setPricingCurrency, goToSignup, betaMode, onSavingsPctChange }) {
  const isDark = theme === 'dark';
  const inBeta = !!betaMode?.enabled;
  const ctaLabel = inBeta ? 'Join the Beta' : null;

  const rates = content?.pricing?.currency_rates || {};
  const [pricing, setPricing] = useState(null);
  const [weeks, setWeeks] = useState(4);
  const [ballRotation, setBallRotation] = useState(0);
  const [capMode, setCapMode] = useState(false);
  const [hasEngaged, setHasEngaged] = useState(false);
  const prevWeeksRef = useRef(4);

  const markEngaged = () => { if (!hasEngaged) setHasEngaged(true); };

  // Founders' Cap config — admin will be able to edit these via CMS once we
  // wire the dedicated form. For now placeholder defaults match the mockup.
  const CAP = useMemo(() => {
    const cms = content?.pricing?.founders_cap || {};
    // Features: prefer the CMS-edited list on `founders_cap.features`.
    // Fallback: pull the existing "lifetime" plan's features so first-load
    // isn't empty for admins who haven't touched the new field yet.
    let features = Array.isArray(cms.features) ? cms.features : null;
    if (!features) {
      const lifetimePlan = (content?.pricing?.plans || []).find(
        (p) => p.id === 'lifetime' || (p.name || '').toLowerCase().includes('lifetime'),
      );
      features = Array.isArray(lifetimePlan?.features) ? lifetimePlan.features : [];
    }
    return {
      name: cms.name || "Founders' Cap",
      headline: cms.headline || 'In from day one.',
      tagline: cms.tagline || "Be in from day one. Your Founders' Cap locks today's price forever \u2014 and earns you a new Cap every contract anniversary.",
      priceEUR: typeof cms.price_eur === 'number' ? cms.price_eur : 59.99,
      features,
      ctaText: cms.cta_text || 'Claim my {name} \u2014 {price}',
      ctaSubtext: cms.cta_subtext || 'One-time payment \u00b7 A new Cap every contract anniversary \u00b7 Price-locked forever',
      founders: {
        total: cms.founders_total || 100,
        claimed: cms.founders_claimed || 23,
      },
    };
  }, [content]);

  // CMS-overridable per-tier labels + descriptions. Defaults match the mockup.
  const tierConfig = useMemo(() => {
    const cms = content?.pricing?.slider_tiers || {};
    return {
      weekly:  { label: cms.weekly?.label  || 'One Game at a Time', desc: cms.weekly?.desc  || 'For less than the price of a pie & Bovril.' },
      monthly: { label: cms.monthly?.label || 'Tournaments',         desc: cms.monthly?.desc || 'Cup run on? Lock it in for a month.' },
      quarter: { label: cms.quarter?.label || 'Pre-Season',          desc: cms.quarter?.desc || 'A 7\u201312 week block. Perfect for pre-season prep.' },
      half:    { label: cms.half?.label    || 'Underage Seasons',    desc: cms.half?.desc    || 'Built around school terms & U12\u2013U16 cycles.' },
      season:  { label: cms.season?.label  || 'Full Season',         desc: cms.season?.desc  || 'Pre-season to play-offs. The whole campaign.' },
    };
  }, [content]);

  // Tango ball thumb image — CMS-overridable, defaults to the user-uploaded asset.
  const ballFile = content?.pricing?.slider_ball_url
    || 'https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/jyfl98do_Untitled%20design%20-%202026-06-12T185108.908.png';

  // Currency config mirrors PricingSection.js
  const currencyConfig = useMemo(() => ({
    eur: { symbol: '\u20AC', label: 'EUR', rate: 1 },
    gbp: { symbol: '\u00A3', label: 'GBP', rate: parseFloat(rates.gbp) || 0.84 },
    usd: { symbol: '$',      label: 'USD', rate: parseFloat(rates.usd) || 1.09 },
  }), [rates]);
  const cc = currencyConfig[pricingCurrency] || currencyConfig.eur;

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  // Listen for CTA-driven arrival at the Pricing section. Any CTA configured
  // to scroll to Pricing dispatches a `pricing-cta-arrived` event right
  // before the smooth scroll begins. We:
  //   • apply any `weeks` prefill immediately so the slider is positioned
  //     by the time it enters view (no visible snap);
  //   • DEFER the thumb pulse until the pricing card's core content is
  //     visible AND the page has stopped scrolling — so the ripple plays
  //     into a still frame, giving it the full one second to register with
  //     the visitor. The IO target is the CTA button (not the slider) so
  //     the pulse waits for the WHOLE card body (badges → weeks → slider →
  //     savings → CTA) to land in the viewport. On small screens where
  //     heading+subtitle can fill the screen before the card body scrolls
  //     into view, this stops the ripple firing off-screen.
  const sliderRef = useRef(null);
  const ctaRef = useRef(null);
  const isVisibleRef = useRef(false);
  const pendingPulseRef = useRef(false);
  const triggerThumbPulse = () => {
    const el = sliderRef.current;
    if (!el) return;
    // Standard restart-CSS-animation trick: remove class, force reflow, add.
    el.classList.remove('pulse-once');
    void el.offsetWidth;
    el.classList.add('pulse-once');
  };
  // Fires `triggerThumbPulse` only once the page has finished scrolling.
  // Uses the native `scrollend` event when available (Chrome/FF 109+) and
  // falls back to a 140ms debounce on `scroll` for older browsers. If the
  // page isn't scrolling at all, fires after a single rAF.
  const firePulseWhenScrollSettled = () => {
    let timer = null;
    const settle = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', onScrollEnd);
      if (timer) clearTimeout(timer);
      requestAnimationFrame(() => triggerThumbPulse());
    };
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(settle, 140);
    };
    const onScrollEnd = () => settle();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scrollend', onScrollEnd, { passive: true });
    // If no scroll event fires in the next 140ms, assume the page was
    // already still and pulse anyway.
    timer = setTimeout(settle, 140);
  };
  // Track viewport visibility of the pricing card's CTA button. When it's
  // fully in view (threshold 1.0) — meaning slider + savings + CTA are all
  // visible — AND a pulse has been queued by a CTA, hand the trigger off
  // to the scroll-settled waiter so the ripple plays into a still frame.
  useEffect(() => {
    const el = ctaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IO support → fall back to firing pulses immediately.
      isVisibleRef.current = true;
      return undefined;
    }
    const obs = new IntersectionObserver(([entry]) => {
      isVisibleRef.current = entry.isIntersecting;
      if (entry.isIntersecting && pendingPulseRef.current) {
        pendingPulseRef.current = false;
        firePulseWhenScrollSettled();
      }
    }, { threshold: 1.0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    const handler = (e) => {
      const w = Number(e?.detail?.weeks);
      if (Number.isFinite(w) && w >= 1 && w <= 52) {
        setWeeks(Math.round(w));
        setHasEngaged(true);
      }
      try { sessionStorage.removeItem('pricingPrefillWeeks'); } catch { /* private-mode safe */ }
      // If the slider's already visible (e.g. nav click from within Pricing
      // anchor, or a no-scroll scenario), wait for scroll-settle then pulse.
      // Otherwise queue — the IO above will pick it up once the slider lands.
      if (isVisibleRef.current) {
        firePulseWhenScrollSettled();
      } else {
        pendingPulseRef.current = true;
      }
    };
    // Slow connections: a stored prefill from before mount still applies on
    // first paint (pulse queued via IO).
    try {
      const pending = sessionStorage.getItem('pricingPrefillWeeks');
      if (pending) {
        const w = Number(pending);
        if (Number.isFinite(w) && w >= 1 && w <= 52) {
          setWeeks(Math.round(w));
          setHasEngaged(true);
          pendingPulseRef.current = true;
          sessionStorage.removeItem('pricingPrefillWeeks');
        }
      }
    } catch { /* private-mode safe */ }
    window.addEventListener('pricing-cta-arrived', handler);
    return () => window.removeEventListener('pricing-cta-arrived', handler);
  }, []);

  // Ball rotation tracks slider movement
  useEffect(() => {
    const delta = weeks - prevWeeksRef.current;
    prevWeeksRef.current = weeks;
    if (delta !== 0) setBallRotation((r) => r + delta * 40);
  }, [weeks]);

  // Anchor per-week rates from live pricing config — currency-converted.
  const anchors = useMemo(() => {
    const w = (Number(pricing?.plans?.weekly?.base_amount) || 2.99) * cc.rate;
    const m = (Number(pricing?.plans?.monthly?.base_amount) || 8.99) * cc.rate;
    const a = (Number(pricing?.plans?.annual?.base_amount) || 83.88) * cc.rate;
    return {
      week1: w,
      week4: m / 4.33,
      week52: a / 52,
      weeklyBase: w,
      monthlyBase: m,
      annualBase: a,
    };
  }, [pricing, cc.rate]);

  // Smooth interpolation: 1→4 from week1→week4, 4→52 from week4→week52
  const perWeek = useMemo(() => {
    if (weeks <= 1) return anchors.week1;
    if (weeks >= 52) return anchors.week52;
    if (weeks <= 4) {
      const tFrac = (weeks - 1) / 3;
      return anchors.week1 + (anchors.week4 - anchors.week1) * tFrac;
    }
    const tFrac = (weeks - 4) / 48;
    return anchors.week4 + (anchors.week52 - anchors.week4) * tFrac;
  }, [weeks, anchors]);

  const totalCost = perWeek * weeks;
  const fullWeeklyCost = anchors.week1 * weeks;
  const savings = Math.max(0, fullWeeklyCost - totalCost);
  const savingsPct = fullWeeklyCost > 0 ? Math.round((savings / fullWeeklyCost) * 100) : 0;

  // Bubble the live savings % out to the parent LandingPage so the Loyalty
  // Ladder's "Today" dot can show it. Zero-savings weeks (weeks=1) fall
  // through to the ladder's "Save" default label.
  useEffect(() => {
    onSavingsPctChange?.(savingsPct);
  }, [savingsPct, onSavingsPctChange]);

  const animPrice = useTweenedNumber(totalCost);
  const animPerWeek = useTweenedNumber(perWeek);
  const animSavings = useTweenedNumber(savings);
  const animStrike = useTweenedNumber(anchors.week1);

  // Tier boundary weeks (CMS-editable). Defaults: tier 1 ends at week 1,
  // tier 2 at week 6, tier 3 at week 12, tier 4 at week 26. Tier 5 covers the rest.
  const breaks = content?.pricing?.slider_tier_breaks || {};
  const tier1Max = Math.max(1, Math.min(52, Number(breaks.weekly)  || 1));
  const tier2Max = Math.max(tier1Max + 1, Math.min(52, Number(breaks.monthly) || 6));
  const tier3Max = Math.max(tier2Max + 1, Math.min(52, Number(breaks.quarter) || 12));
  const tier4Max = Math.max(tier3Max + 1, Math.min(52, Number(breaks.half)    || 26));

  // Pick the current tier based on slider position
  const tier =
    weeks <= tier1Max ? { id: 'weekly',  ...tierConfig.weekly,  accent: '#3b82f6' } :
    weeks <= tier2Max ? { id: 'monthly', ...tierConfig.monthly, accent: '#0ea5e9' } :
    weeks <= tier3Max ? { id: 'quarter', ...tierConfig.quarter, accent: '#06b6d4' } :
    weeks <= tier4Max ? { id: 'half',    ...tierConfig.half,    accent: '#10b981' } :
                        { id: 'season',  ...tierConfig.season,  accent: '#f59e0b' };

  // Intro copy shown before user engages with the slider. CMS-editable.
  const introLabel = content?.pricing?.slider_intro_label || 'Pick a Plan';
  const introDesc  = content?.pricing?.slider_intro_desc  || 'Drag the ball to find your perfect plan.';
  const displayLabel = hasEngaged ? tier.label : introLabel;
  const displayDesc  = hasEngaged ? tier.desc  : introDesc;

  const fmt = (n) => `${cc.symbol}${(Math.round(n * 100) / 100).toFixed(2)}`;

  // CMS-editable copy for the pricing grid headings + sign CTA template.
  // The CTA template supports two placeholders: {weeks} (e.g. "13 weeks") and
  // {price} (e.g. "€25.86"). Both get substituted at render time.
  const perWeekLabel = content?.pricing?.slider_per_week_label || 'Per week';
  const totalLabel = content?.pricing?.slider_total_label || 'Total contract';
  const signTemplate = content?.pricing?.slider_sign_cta_template || 'Sign for {weeks} \u2014 {price}';
  const weeksWord = weeks === 1 ? '1 week' : `${weeks} weeks`;
  const signCtaText = signTemplate
    .replace('{weeks}', weeksWord)
    .replace('{price}', fmt(animPrice));

  // Founders' Cap derived figures
  const capPrice = CAP.priceEUR * cc.rate;
  const paybackMonthsRaw = anchors.annualBase > 0 ? (capPrice / anchors.annualBase) * 12 : 0;
  const paybackMonths = Math.round(paybackMonthsRaw);
  const paybackYears = Math.round((paybackMonthsRaw / 12) * 10) / 10;
  const paybackDisplay = paybackMonthsRaw < 12 ? `${paybackMonths} months` : `${paybackYears} yrs`;
  const fiveYearAnnualCost = anchors.annualBase * 5;
  const fiveYearSavings = Math.max(0, fiveYearAnnualCost - capPrice);
  const foundersPct = Math.round((CAP.founders.claimed / CAP.founders.total) * 100);
  const foundersLeft = CAP.founders.total - CAP.founders.claimed;

  const anchorMarks = [
    { w: 1,  label: 'Wk 1' },
    { w: 4,  label: '4 wk',  desktopOnly: true },
    { w: 13, label: '3 mo',  desktopOnly: true },
    { w: 26, label: '6 mo' },
    { w: 52, label: 'Season' },
  ];

  const handleSignClick = () => {
    trackPricingClick?.({ plan_id: tier.id, weeks });
    goToSignup?.();
  };
  const handleCapClick = () => {
    trackPricingClick?.({ plan_id: 'founders_cap', weeks: 0 });
    goToSignup?.();
  };

  // Currency toggle — mirrors PricingSection.js so the slider variant matches
  // the cards variant visually. Rendered above the pricing card by default;
  // admin can flip it below via `content.pricing.currency_switcher_position`.
  const renderCurrencyToggle = (extraClass = '') => (
    <div
      className={`flex items-center justify-center gap-1 mt-6 ${extraClass}`}
      data-testid="currency-toggle"
    >
      {[
        { key: 'eur', symbol: '\u20AC', label: 'EUR' },
        { key: 'gbp', symbol: '\u00A3', label: 'GBP' },
        { key: 'usd', symbol: '$', label: 'USD' },
      ].map((c) => (
        <button
          key={c.key}
          onClick={() => setPricingCurrency(c.key)}
          className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
            pricingCurrency === c.key
              ? 'bg-[#007a33] text-white'
              : isDark
              ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          data-testid={`currency-${c.key}-btn`}
        >
          {c.symbol} {c.label}
        </button>
      ))}
    </div>
  );

  return (
    <section
      id="pricing"
      data-section="pricing"
      data-testid="pricing-slider-section"
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{
        '--ball-thumb': `url(${ballFile})`,
        '--ball-rotation': `${ballRotation}deg`,
        '--fill-frac': `${(weeks - 1) / 51}`,
        '--thumb-bg': 'transparent',
        '--thumb-border': '0 solid transparent',
        '--thumb-size': '100%',
      }}
    >
      <style>{`
        @keyframes pricing-slider-price-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        .pricing-slider-section .price-pop { animation: pricing-slider-price-pop 0.35s ease-out; }

        @keyframes pricing-slider-pulse-cap {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.5), 0 10px 25px -5px rgba(251, 191, 36, 0.3); }
          50%      { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0), 0 14px 30px -5px rgba(251, 191, 36, 0.5); }
        }
        .pricing-slider-section .animate-pulse-trophy { animation: pricing-slider-pulse-cap 2.2s ease-in-out infinite; }
      `}</style>

      <div className="max-w-4xl mx-auto pricing-slider-section">
        {/* Header */}
        <div className="text-center mb-10">
          {content?.pricing?.badge && (
            <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]">
              {content.pricing.badge}
            </Badge>
          )}
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {content?.pricing?.title || 'Sign Your Contract.'}
          </h2>
          <p className={`${t?.textSecondary || (isDark ? 'text-gray-300' : 'text-gray-600')} text-lg max-w-2xl mx-auto`}>
            {content?.pricing?.subtitle || 'Drag the ball. Pick how long you want to manage with us \u2014 the longer the deal, the lower the per-week rate.'}
          </p>
          {/* Currency selector — shown above by default. Admin can move
              below the card via `content.pricing.currency_switcher_position`. */}
          {(content?.pricing?.currency_switcher_position || 'above') === 'above' && renderCurrencyToggle()}
        </div>

        {/* Weekly contract card */}
        {!capMode && (
          <div
            className={`relative rounded-2xl border p-6 sm:p-8 mb-8 transition-colors ${
              isDark ? 'bg-gray-900/60 border-gray-800' : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50'
            }`}
            data-testid="contract-card"
          >
            {/* Tier badge (green, left) + Founders' Cap pill (gold, right) —
                pill mirrors the gold badge shown when the Cap state is open
                so the two corners of the card feel paired. */}
            {/* Mobile: stacked full-width chips, Founders' Cap on top; the
                gold cap wins the eye then the tier context sits below. On
                sm+ they revert to the original horizontal layout (tier left,
                cap right). DOM order stays tier→cap so screen readers still
                announce the tier first; `flex-col-reverse` swaps the visual
                order on mobile only. */}
            <div className="flex flex-col-reverse gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <span
                className={`inline-flex items-center justify-center w-full px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider text-white transition-colors sm:w-auto sm:py-1 ${
                  hasEngaged ? 'bg-[#007a33]' : 'bg-gray-500 animate-pulse'
                }`}
                data-testid="tier-badge"
              >
                {displayLabel}
              </span>
              <button
                type="button"
                onClick={() => setCapMode(true)}
                className={`inline-flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow hover:shadow-md hover:scale-105 transition-all sm:w-auto sm:py-1 sm:shrink-0 ${totalCost > capPrice ? 'animate-pulse-trophy' : ''}`}
                data-testid="founders-cap-tile-btn"
                aria-label={`Open Founders' Cap — ${fmt(capPrice)} one-time`}
              >
                <span>{CAP.name}</span>
                {/* Price uses the currency-formatted `capPrice` (already
                    responsive to the visitor's EUR/GBP/USD toggle). The dot
                    separator groups it visually with the label; the chevron
                    signals "there is more here — tap me". */}
                <span className="opacity-80" aria-hidden="true">&middot;</span>
                <span data-testid="founders-cap-tile-price">{fmt(capPrice)}</span>
                <ChevronRight className="w-3.5 h-3.5 -mr-0.5" strokeWidth={2.5} aria-hidden="true" />
              </button>
            </div>

            {/* Description (under both pills) */}
            {displayDesc && (
              <p
                className={`mb-6 text-xs sm:text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                data-testid="tier-desc"
              >
                {displayDesc}
              </p>
            )}

            {/* Headline contract length */}
            <div className="text-center mb-8" data-testid="weeks-big">
              <div className={`text-7xl sm:text-8xl font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {weeks}
              </div>
              <div className={`text-3xl sm:text-4xl font-bold mt-1 opacity-70 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {weeks === 1 ? 'week' : 'weeks'}
              </div>
            </div>

            {/* Slider */}
            <div className="mb-2 px-1">
              <input
                ref={sliderRef}
                type="range"
                min="1" max="52" step="1"
                value={weeks}
                onChange={(e) => { markEngaged(); setWeeks(parseInt(e.target.value, 10)); }}
                className="contract-slider w-full"
                aria-label="Contract length in weeks"
                data-testid="contract-length-slider"
              />
            </div>

            {/* Anchor labels */}
            <div className="relative h-6 mb-6 select-none">
              {anchorMarks.map(({ w, label, desktopOnly }) => {
                const frac = (w - 1) / 51;
                const active = Math.abs(weeks - w) <= 0;
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => { markEngaged(); setWeeks(w); }}
                    className={`absolute -translate-x-1/2 text-[10px] sm:text-xs font-semibold transition-colors ${desktopOnly ? 'hidden md:block' : ''} ${
                      active ? 'text-[#007a33]' : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    style={{ left: `calc(16px + ${frac} * (100% - 32px))` }}
                    data-testid={`anchor-${w}`}
                  >
                    <span className={`block w-1 h-1 mx-auto mb-1 rounded-full ${active ? 'bg-[#007a33]' : isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Pricing grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
              <div className={`p-3 sm:p-4 rounded-xl ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-gray-50 border border-gray-200'}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{perWeekLabel}</div>
                <div className="flex items-baseline gap-2">
                  <div className={`text-lg sm:text-3xl font-black price-pop ${isDark ? 'text-white' : 'text-gray-900'}`} key={`pw-${weeks}-${pricingCurrency}`} data-testid="per-week-price">
                    {fmt(animPerWeek)}
                  </div>
                  {weeks > 1 && (
                    <div className={`hidden sm:block text-sm line-through ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-testid="per-week-strike">
                      {fmt(animStrike)}
                    </div>
                  )}
                </div>
              </div>

              <div className={`p-3 sm:p-4 rounded-xl ${isDark ? 'bg-[#007a33]/15 border border-[#007a33]/40' : 'bg-[#007a33]/5 border border-[#007a33]/30'}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1 text-[#007a33]">{totalLabel}</div>
                <div className={`text-lg sm:text-3xl font-black price-pop ${isDark ? 'text-white' : 'text-gray-900'}`} key={`total-${weeks}-${pricingCurrency}`} data-testid="total-price">
                  {fmt(animPrice)}
                </div>
              </div>

              <div className={`p-3 sm:p-4 rounded-xl ${savings > 0
                ? 'bg-amber-500/10 border border-amber-500/40'
                : (isDark ? 'bg-black/30 border border-gray-800' : 'bg-gray-50 border border-gray-200')
              }`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${savings > 0 ? 'text-amber-500' : isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  <span data-testid="savings-pct">
                    <span className="sm:hidden">Save {savingsPct}%</span>
                    <span className="hidden sm:inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />You save {savingsPct}% v Weekly Rate</span>
                  </span>
                </div>
                <div className={`text-lg sm:text-3xl font-black price-pop ${savings > 0 ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`} key={`save-${weeks}-${pricingCurrency}`} data-testid="savings-amount">
                  {fmt(animSavings)}
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button
              ref={ctaRef}
              size="lg"
              onClick={handleSignClick}
              className="w-full bg-[#007a33] hover:bg-[#005c26] text-white text-base py-6 rounded-xl"
              data-testid="contract-sign-btn"
            >
              <Ticket className="w-5 h-5 mr-2 hidden sm:inline-block" />
              {ctaLabel || signCtaText}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p className={`text-center text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {content?.pricing?.footerText || 'Full premium access \u00b7 cancel any time \u00b7 no credit card required to start'}
            </p>
          </div>
        )}

        {/* Founders' Cap state */}
        {capMode && (
          <div
            className={`relative overflow-hidden rounded-2xl border-2 p-6 sm:p-8 mb-8 transition-colors ${
              isDark
                ? 'bg-gradient-to-br from-amber-900/20 via-yellow-900/10 to-amber-900/20 border-amber-400/50'
                : 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-amber-400/60 shadow-xl shadow-amber-200/40'
            }`}
            data-testid="founders-cap-mode-card"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

            <div className="flex items-center justify-between mb-6 relative">
              <button
                type="button"
                onClick={() => setCapMode(false)}
                className={`text-xs font-semibold flex items-center gap-1.5 hover:underline ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                data-testid="back-to-weekly-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <span
                className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow flex items-center gap-1.5"
                data-testid="founders-cap-badge"
              >
                <FoundersCapIcon className="w-4 h-4" />
                {CAP.name}
              </span>
            </div>

            <div className="text-center mb-6 relative">
              <FoundersCapIcon className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-2 drop-shadow-lg" />
              <div className={`text-4xl sm:text-5xl font-black mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {CAP.headline}
              </div>
              <p className={`text-sm sm:text-base max-w-md mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {CAP.tagline}
              </p>
            </div>

            <div className="text-center mb-6 relative">
              <div
                className={`text-6xl sm:text-7xl font-black leading-none mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}
                data-testid="founders-cap-price"
              >
                {fmt(capPrice)}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                one-time payment &middot; no renewals
              </div>
            </div>

            {/* Features — inherited from the Lifetime Season Ticket plan card so
                Founders' Cap purchasers see everything they get. Editable in
                the CMS at Pricing → Slider → Founders' Cap → Features. */}
            {CAP.features?.length > 0 && (
              <ul
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 relative"
                data-testid="founders-cap-features"
              >
                {CAP.features.map((f, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
            )}

            <div
              className={`rounded-xl border p-4 mb-6 relative ${isDark ? 'bg-black/40 border-amber-400/30' : 'bg-white/70 border-amber-300/50'}`}
              data-testid="founders-scarcity"
            >
              <div className="flex items-center justify-between text-xs font-semibold mb-2">
                <span className="text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                  {CAP.name} &middot; First {CAP.founders.total}
                </span>
                <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-testid="founders-count">
                  {CAP.founders.claimed} of {CAP.founders.total} claimed &middot; {foundersLeft} left
                </span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all"
                  style={{ width: `${foundersPct}%` }}
                />
              </div>
              <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Only the first {CAP.founders.total} coaches earn the {CAP.name}. Locks today&apos;s price + every feature we ever ship. After {CAP.founders.total}, this offer disappears.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 relative">
              <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-white/80 border border-amber-200'}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>vs annual</div>
                <div className={`text-base sm:text-lg font-black leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Pays for itself<br />in ~{paybackDisplay}
                </div>
              </div>
              <div className={`p-3 rounded-xl text-center ${isDark ? 'bg-black/30 border border-gray-800' : 'bg-white/80 border border-amber-200'}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>After 5 yrs</div>
                <div className="text-base sm:text-lg font-black leading-tight text-amber-600 dark:text-amber-400">
                  {fmt(fiveYearSavings)} saved
                </div>
              </div>
            </div>

            <Button
              size="lg"
              onClick={handleCapClick}
              className="relative w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white text-lg py-6 rounded-xl shadow-lg shadow-amber-500/30"
              data-testid="founders-cap-cta-btn"
            >
              {ctaLabel || CAP.ctaText.replace('{name}', CAP.name).replace('{price}', fmt(capPrice))}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p
              className={`text-center text-xs mt-3 relative ${isDark ? 'text-gray-500' : 'text-gray-500'}`}
              data-testid="founders-cap-cta-subtext"
              dangerouslySetInnerHTML={{
                __html: CAP.ctaSubtext.replace('{name}', CAP.name).replace('{price}', fmt(capPrice)),
              }}
            />
          </div>
        )}

        {/* Currency selector — only shown here when admin has placed it below. */}
        {content?.pricing?.currency_switcher_position === 'below' && renderCurrencyToggle('mt-6')}
      </div>
    </section>
  );
}

export default PricingSliderSection;
